import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, Activity, Clock } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminHealthChecks() {
  const navigate = useNavigate();
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const load = async () => {
    try {
      const [latestRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/health-checks/latest`, authHeaders()),
        axios.get(`${API_URL}/api/admin/health-checks?limit=30`, authHeaders()),
      ]);
      setLatest(latestRes.data.run);
      setHistory(historyRes.data.runs || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/health-checks/run-now`, {}, authHeaders());
      setLatest(res.data);
      // refresh the history list to include the new run
      const historyRes = await axios.get(`${API_URL}/api/admin/health-checks?limit=30`, authHeaders());
      setHistory(historyRes.data.runs || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="health-loading">
        <div className="text-neutral-400">Loading health checks…</div>
      </div>
    );
  }

  const greenRun = latest && latest.failed === 0;

  return (
    <AdminLayout>
    <div className="min-h-screen bg-black text-white" data-testid="admin-health-checks">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="p-2 rounded-full hover:bg-white/5 text-neutral-400" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
                <Activity className="w-7 h-7" /> Health Checks
              </h1>
              <p className="text-neutral-400 text-sm mt-1">
                Daily 4 AM PT sweep of {latest?.total_checks ?? '?'} critical endpoints. Alert email fires only on failures.
              </p>
            </div>
          </div>
          <button
            onClick={runNow}
            disabled={running}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 disabled:opacity-50"
            data-testid="run-now-btn"
          >
            <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running…' : 'Run now'}
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-red-300 text-sm" data-testid="health-error">
            {error}
          </div>
        )}

        {/* Latest run summary */}
        {latest ? (
          <div
            className={`rounded-xl border p-5 mb-8 ${greenRun ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}
            data-testid="latest-run-summary"
          >
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500">
                  <Clock className="w-3.5 h-3.5" />
                  Last run · {new Date(latest.started_at).toLocaleString()}
                  {latest.triggered_by ? ` · ${latest.triggered_by.replace('_', ' ')}` : ''}
                </div>
                <h2 className={`text-2xl font-bold mt-2 ${greenRun ? 'text-emerald-300' : 'text-red-300'}`}>
                  {greenRun ? 'All systems healthy' : `${latest.failed} check${latest.failed === 1 ? '' : 's'} failing`}
                </h2>
                <p className="text-sm text-neutral-400 mt-1">
                  {latest.passed} / {latest.total_checks} passing
                  {latest.critical_failures > 0 && (
                    <span className="text-red-300"> · {latest.critical_failures} CRITICAL</span>
                  )}
                  {' '}· completed in {latest.elapsed_seconds}s
                </p>
              </div>
              <div className={`text-5xl font-black ${greenRun ? 'text-emerald-400' : 'text-red-400'}`}>
                {latest.passed}/{latest.total_checks}
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-neutral-500 mb-8">
            No health check has run yet. Click <strong>Run now</strong> to seed the first one.
          </div>
        )}

        {/* Per-check breakdown */}
        {latest?.results?.length > 0 && (
          <section className="mb-12" data-testid="checks-breakdown">
            <h3 className="text-lg font-bold mb-3">Last run breakdown</h3>
            <div className="rounded-xl border border-white/10 bg-neutral-900/40 overflow-hidden">
              {latest.results.map((r) => (
                <div
                  key={r.id}
                  className="grid grid-cols-12 items-center px-5 py-3 border-b border-white/5 last:border-0"
                  data-testid={`check-row-${r.id}`}
                >
                  <div className="col-span-1">
                    {r.status === 'pass' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <AlertTriangle className={`w-5 h-5 ${r.critical ? 'text-red-400' : 'text-amber-400'}`} />
                    )}
                  </div>
                  <div className="col-span-7">
                    <div className="font-semibold text-sm">{r.description}</div>
                    {r.error && (
                      <div className="text-xs text-red-300 mt-0.5">{r.error}</div>
                    )}
                  </div>
                  <div className="col-span-2 text-right text-xs text-neutral-500">
                    HTTP {r.http_status ?? '—'}
                  </div>
                  <div className="col-span-2 text-right text-xs text-neutral-500">
                    {r.elapsed_ms != null ? `${r.elapsed_ms}ms` : '—'}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* History */}
        {history.length > 1 && (
          <section data-testid="health-history">
            <h3 className="text-lg font-bold mb-3">Recent runs</h3>
            <div className="rounded-xl border border-white/10 bg-neutral-900/40 overflow-hidden">
              <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-wider text-neutral-500 border-b border-white/5">
                <div className="col-span-4">When</div>
                <div className="col-span-2">Trigger</div>
                <div className="col-span-2 text-right">Passed</div>
                <div className="col-span-2 text-right">Failed</div>
                <div className="col-span-2 text-right">Elapsed</div>
              </div>
              {history.map((r) => (
                <div key={r.run_id || r.started_at} className="grid grid-cols-12 px-5 py-3 text-sm border-b border-white/5 last:border-0">
                  <div className="col-span-4 text-neutral-300">{new Date(r.started_at).toLocaleString()}</div>
                  <div className="col-span-2 text-neutral-500 text-xs">{(r.triggered_by || '').replace('_', ' ')}</div>
                  <div className="col-span-2 text-right text-emerald-400 font-semibold">{r.passed}</div>
                  <div className={`col-span-2 text-right font-semibold ${r.failed > 0 ? 'text-red-400' : 'text-neutral-500'}`}>{r.failed}</div>
                  <div className="col-span-2 text-right text-neutral-500 text-xs">{r.elapsed_seconds}s</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
    </AdminLayout>
  );
}
